
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import { ObjectType, TitleListType } from '@/@types/graphql-types';
import AdUnit from '@/components/AdUnit.vue';
import LoginButton from '@/components/LoginButton.vue';
import QuickActionBar from '@/components/QuickActionBar.vue';
import TitleInfo from '@/components/details/TitleInfo.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { QuickActionBarProps } from '@/interfaces/components/QuickActionBar';
import { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import { TitleListName } from '@/interfaces/responses/title-list';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { trackTitleDetailRedesign } from '@/pages/experiments/title-detail-redesign/tracking';

@Component({
	name: 'TitleSidebar',
	components: {
		QuickActionBar,
		TitleInfo,
		PosterGraphql,
		AdUnit,
		LoginButton,
	},
})
export default class TitleSidebar extends Vue {
	location = 'TitleSidebar';
	TitleListType = TitleListType;

	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ required: true }) title: TitleDetail;
	@Prop() titleContentType: TitleContentType | undefined;
	@Prop({ default: null }) influencedSponsoredAd: SponsoredAdFragment;

	get seenEpisodeCount() {
		if (this.title.__typename === 'Show') return this.title.seenState.seenEpisodeCount;
	}

	get posterGraphqlProps(): PosterGraphqlProps {
		return {
			titleId: this.title.id,
			titleObjectId: this.title.objectId,
			titleObjectType: this.title.objectType,
			titleName: this.title.content.title,
			titlePosterUrl: this.title.content.posterUrl ?? '',
			titleBackdrops: this.title.content.backdrops,
			titleScoring: this.title.content.scoring,
			titleAvailableTo: this.title.availableTo,
			titleUpcomingReleases: this.title.content.upcomingReleases,
			seenEpisodeCount: this.seenEpisodeCount,
			isInCustomList: this.isInCustomList,
			isTvShowTracked: this.isTvShowTracked,
			isInWatchlist: this.isTitleInList(TitleListType.WatchNext),
			isInSeenList: this.isTitleInList(TitleListType.CaughtUp),
			isInLikeList: this.isTitleInList(TitleListType.Likelist),
			isInDislikeList: this.isTitleInList(TitleListType.Dislikelist),
			imageProfile: 'poster_detail',
			noRadius: 'bottom',
			lazy: true,
			showAvailabilityBadge: true,
		};
	}

	get quickActionBarProps(): QuickActionBarProps {
		const props: QuickActionBarProps = {
			titleId: this.title.id,
			titleName: this.title.content.title,
			titleObjectId: this.title.objectId,
			titleObjectType: this.title.objectType,
			titleBackdrops: this.title.content.backdrops,
			titleContentType: this.titleContentType,
			titlePosterUrl: this.title.content.posterUrl ?? '',
			titleSeenEpisodeCount: this.seenEpisodeCount,
			isInWatchlist: this.isTitleInList(TitleListType.WatchNext),
			isInCustomList: this.isInCustomList,
			isTvShowTracked: this.isTvShowTracked,
			isInSeenList: this.isTitleInList(TitleListType.CaughtUp),
			isInLikeList: this.isTitleInList(TitleListType.Likelist),
			isInDislikeList: this.isTitleInList(TitleListType.Dislikelist),
			without: [TitleListName.SEENLIST],
			additionalContexts: this.additionalContexts,
			isTitleUpcoming: this.isTitleUpcoming,
			isTitleReleased: this.title.content.isReleased,
			likelistAdditions: this.interactions?.likelistAdditions || 0,
			dislikelistAdditions: this.interactions?.dislikelistAdditions || 0,
			jwVotes: this.interactions?.votesNumber || 0,
			influencedSponsoredAd: this.influencedSponsoredAd,
		};

		if (this.title.__typename === 'Season') {
			props.seasonNumber = this.title.content.seasonNumber;
			props.showDetails = {
				id: this.title.show?.id as string,
				objectType: this.title.show?.objectType as ObjectType,
				objectId: this.title.show?.objectId as number,
				title: this.title.show?.content.title,
				isReleased: this.title.show?.content.isReleased,
			};
		}

		return props;
	}

	get interactions() {
		if (this.title.__typename === 'Season' && (this.title.show?.totalSeasonCount as number) <= 1) {
			return null;
		}

		return this.title.content.interactions;
	}

	get isTitleUpcoming() {
		return !this.title?.content.isReleased;
	}

	get totalSeasonCount() {
		if (this.title.__typename === 'Season') {
			return this.title.show?.totalSeasonCount;
		}

		if (this.title.__typename === 'Show') {
			return this.title.totalSeasonCount;
		}

		return 0;
	}

	get isInCustomList() {
		const customListEntries =
			this.title.__typename === 'Season' ? this.title.show?.customlistEntries : this.title.customlistEntries;

		return !!customListEntries?.length;
	}

	get isTvShowTracked() {
		if (this.title.__typename === 'Season') {
			return !!this.title.show?.tvShowTrackingEntry?.createdAt;
		}

		return this.title.__typename === 'Show' ? !!this.title.tvShowTrackingEntry?.createdAt : false;
	}

	isTitleInList(listName: TitleListType) {
		switch (listName) {
			case TitleListType.WatchNext:
				if (this.title.__typename === 'Season') {
					return !!this.title?.show?.watchlistEntryV2?.createdAt;
				}
				return !!this.title?.watchlistEntryV2?.createdAt;
			case TitleListType.Likelist:
				if (this.title.__typename === 'Season') {
					return !!this.title?.show?.likelistEntry?.createdAt;
				}
				return !!this.title?.likelistEntry?.createdAt;
			case TitleListType.Dislikelist:
				if (this.title.__typename === 'Season') {
					return !!this.title?.show?.dislikelistEntry?.createdAt;
				}
				return !!this.title?.dislikelistEntry?.createdAt;
			case TitleListType.CaughtUp:
				return this.title.__typename === 'Movie'
					? !!this.title?.seenlistEntry?.createdAt
					: this.title?.seenState?.progress === 100;
		}
	}
	//TITLE_DETAIL_REDESIGN
	removeAdClicked = () => {
		trackTitleDetailRedesign({ action: 'click', label: 'RemoveAds' });
	};
	//TITLE_DETAIL_REDESIGN
}
